import React from 'react';

import type { AuthPageShellProps } from './AuthPageShell.types';

import {
  AmbientBloom,
  Card,
  KanjiWatermark,
  Text,
  Wordmark,
} from '@/components/ui';

/**
 * AuthPageShell — the two-panel frame every signed-out account page shares
 * (log in, register, forgot + reset password, and the emailed links' pages): a
 * desktop brand panel with a kanji watermark beside the form card. Pages vary
 * only the brand copy and the character, so moving between them reads as one
 * flow.
 *
 * The brand panel keeps a fixed gap under the wordmark on every page. Beside a
 * form taller than the panel, the plate takes the extra height, so the copy
 * never floats down the panel.
 */
export function AuthPageShell({
  headline,
  lede,
  brandExtra,
  kanji = '道',
  children,
}: AuthPageShellProps): React.JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center bg-contrast-950 px-4 py-8 sm:px-6 lg:px-8">
      <AmbientBloom size="xl" className="fixed -left-40 -top-40" />
      <AmbientBloom
        size="lg"
        tone="primary-400"
        className="fixed -bottom-40 -right-40"
      />

      <div className="relative z-10 flex w-full max-w-5xl gap-6 lg:gap-8">
        {/* Brand panel - desktop only */}
        <Card
          padding="lg"
          className="hidden flex-col gap-12 lg:flex lg:w-1/2 xl:p-12"
        >
          <Wordmark size={32} />

          <div className="flex flex-1 flex-col">
            <Text variant="heading-xxl" className="mb-4 font-bold">
              {headline}
            </Text>
            <Text color="secondary" className="mb-8">
              {lede}
            </Text>

            {brandExtra}

            <KanjiWatermark kanji={kanji} className="grow" />
          </div>
        </Card>

        {/* Form panel */}
        <div className="flex w-full flex-col items-center justify-center lg:w-1/2">
          <Wordmark size={32} className="mb-8 lg:hidden" />

          <Card padding="md" className="w-full max-w-md sm:p-8">
            {children}
          </Card>
        </div>
      </div>
    </div>
  );
}

export default AuthPageShell;
